import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Divider, ListItem, capitalize } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ListCell, ListHeader, ListRow, ViewNotesModal } from '/src/components';
import { useAssetStore } from '/src/context';
import { Asset, Note } from '/src/lib/models';
import { TListAssetsRequest, Totals } from '/src/lib/types';
import * as utils from '/src/utils';

export type AssetListViewOptions = {
  /**
   * Display notes
   */
  notes?: boolean;
  /**
   * Display name
   */
  name?: boolean;
  /**
   * Display value
   */
  value?: boolean;
  /**
   * Display original value
   */
  originalValue?: boolean;
  /**
   * Display current value
   */
  currentValue?: boolean;
  /**
   * Display program
   */
  program?: boolean;
  /**
   * Display status
   */
  status?: boolean;
  /**
   * Display total Payments
   */
  totalPayments?: boolean;
  /**
   * Display total Adjustments
   */
  totalAdjustments?: boolean;
  /**
   * Display associated Loan
   */
  loan?: boolean;
  [key: string]: boolean | undefined;
};

export type AssetsListProps = {
  /**
   * List of Assets to display
   */
  data?: Asset[];
  /**
   * Query to include when fetching Assets
   */
  query?: TListAssetsRequest;
  /**
   * Sets what data to display
   */
  viewOptions: AssetListViewOptions;
};

/**
 * Assets List
 */
export const AssetsList: React.FC<AssetsListProps> = observer(({ data, query, viewOptions }) => {
  const defaultSortType = 'sequenceNumber';
  const defaultSortReverse = false;
  const rowHeight = 48;

  /** Context **/
  const assetStore = useAssetStore();

  /** State **/
  const [assets, setAssets] = useState<Asset[]>([]);
  const [totals, setTotals] = useState<Totals>({});
  const [resolved, setResolved] = useState(false);
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);

  // View Notes popup shows if `notes` is defined
  const [notes, setNotes] = useState<Note[]>();
  const [notesName, setNotesName] = useState<string>();

  /** Computed **/

  const listData = useMemo(() => {
    if (assets) {
      const list = assets.toSorted((a, b) => ((a as any)[sortType] > (b as any)[sortType] ? 1 : -1));
      if (sortReverse) list.reverse();

      return list;
    } else return [];
  }, [assets, sortType, sortReverse]);

  /** Methods **/

  const formatData = (data: Asset[]) => {
    // Prepare totals
    const totals: Totals = {};
    totals.value = 0;
    totals.originalValue = 0;
    totals.currentValue = 0;
    totals.totalPayments = 0;
    totals.totalAdjustments = 0;

    const assets = data.map((asset) => {
      // Combine
      asset.notes = [...asset.socketComments, ...asset.companyComments].sort((a, b) =>
        a.created > b.created ? 1 : -1
      );

      // Calculate values
      asset.currentValue = asset.originalValue + (asset.totalAdjustments ?? 0) - (asset.totalPayments || 0);

      // Calculate totals
      totals.value += asset.value;
      totals.originalValue += asset.originalValue;
      totals.currentValue += asset.currentValue;
      if (asset.totalPayments) totals.totalPayments += asset.totalPayments;
      if (asset.totalAdjustments) totals.totalAdjustments += asset.totalAdjustments;

      // Store
      return asset;
    });

    // Save data
    setAssets(assets);
    setTotals(totals);

    // Set as resolved
    setResolved(true);
  };

  const showNotesPopup = (notes?: Note[], name?: string) => {
    setNotes(notes ?? []);
    setNotesName(name);
  };

  /**
   * Sets sortType and sortReverse based on given sortType
   * @param type string sortType to use
   */
  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  /** Effects **/

  useEffect(() => {
    if (data) {
      formatData(data);
    } else if (query && Object.values(query).length) {
      assetStore.query(query).then((assets) => assets && formatData(assets));
    }
  }, [data, query]);

  /** Render **/
  return (
    <Box>
      {/* HEADER */}
      {!!listData.length && (
        <ListHeader
          items={[
            { flex: 5, propName: 'notes', label: 'Notes' },
            { propName: 'name', label: 'Name' },
            { propName: 'value', label: 'Value' },
            { propName: 'originalValue', label: 'Original Value' },
            { propName: 'currentValue', label: 'Current Value', textAlign: 'right' },
            { propName: 'program', label: 'Program', textAlign: 'right' },
            { propName: 'status', label: 'Status', textAlign: 'right' },
            { propName: 'totalPayments', label: 'Total Payments', textAlign: 'right' },
            { propName: 'totalAdjustments', label: 'Total Adjustments', textAlign: 'right' },
            { propName: 'loan', label: 'Deal', textAlign: 'right' },
          ]}
          viewOptions={viewOptions}
          sortType={sortType}
          sortReverse={sortReverse}
          sortList={sortList}
        />
      )}

      {!!listData.length && <Divider />}

      {/* LIST */}
      {resolved && !!listData.length && (
        <Box component="md-content" height={listData.length * rowHeight + 'px'} maxHeight={9 * rowHeight + 'px'}>
          {listData.map((asset, i) => (
            <ListRow
              key={asset._id}
              viewOptions={viewOptions}
              items={[
                {
                  propName: 'notes',
                  isNotes: true,
                  flex: 5,
                  hide: !asset.companyComments.length && !asset.socketComments.length,
                  showNotesPopup: () => showNotesPopup(asset.notes, asset.name),
                },
                { propName: 'name', label: 'Name', content: asset.name, url: `/assets/${asset._id}` },
                { propName: 'value', label: 'Value', content: utils.currency.format(asset.value) },
                {
                  propName: 'originalValue',
                  label: 'Original Value',
                  content: utils.currency.format(asset.originalValue),
                },
                {
                  propName: 'currentValue',
                  label: 'Current Value',
                  textAlign: 'right',
                  content: asset.currentValue ? utils.currency.format(asset.currentValue) : '',
                },
                { propName: 'program', label: 'Program', textAlign: 'right', content: asset.program },
                { propName: 'status', label: 'Status', textAlign: 'right', content: capitalize(asset.status) },
                {
                  propName: 'totalPayments',
                  label: 'Total Payments',
                  textAlign: 'right',
                  content: asset.totalPayments ? utils.currency.format(asset.totalPayments) : '',
                },
                {
                  propName: 'totalAdjustments',
                  label: 'Total Adjustments',
                  textAlign: 'right',
                  content: asset.totalAdjustments ? utils.currency.format(asset.totalAdjustments) : '',
                },
                {
                  propName: 'loan',
                  label: 'Deal',
                  textAlign: 'right',
                  content: asset.loan.name,
                  url: `/deals/${asset.loan._id}`,
                },
              ]}
              rowIndex={i}
            />
          ))}
        </Box>
      )}

      <Divider />

      {/* TOTALS */}
      {!!listData.length && (
        <ListItem sx={{ height: rowHeight }}>
          <Box
            display="flex"
            flex="100"
            justifyContent="space-between"
            alignItems="center"
            className={`medium md-subhead-sm text-center ${listData.length > 8 && 'padForScrollbar'}`}
          >
            {[
              { flex: 5, propName: 'notes' },
              { propName: 'name' },
              { propName: 'value' },
              { propName: 'originalValue' },
              { propName: 'currentValue', textAlign: 'right' },
              { propName: 'program', textAlign: 'right' },
              { propName: 'status', textAlign: 'right' },
              { propName: 'totalPayments', textAlign: 'right' },
              { propName: 'totalAdjustments', textAlign: 'right' },
              { propName: 'loan', textAlign: 'right' },
            ]
              .filter((item) => viewOptions[item.propName])
              .map(({ propName, ...itemProps }, i) => (
                <ListCell
                  key={`${propName}-${i}`}
                  content={i === 0 ? 'Totals' : totals[propName] ? utils.currency.format(totals[propName]) : ''}
                  {...itemProps}
                />
              ))}
          </Box>
        </ListItem>
      )}

      {resolved && !listData.length && (
        <ListItem>
          <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
            <Box component="span">No Assets found.</Box>
          </Box>
        </ListItem>
      )}

      {!resolved && (
        <ListItem>
          <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
            <CircularProgress className="md-primary" md-mode="indeterminate" />
          </Box>
        </ListItem>
      )}

      {notes && (
        <ViewNotesModal
          notes={notes}
          name={notesName}
          open={!!notes}
          setOpen={(open) => !open && setNotes(undefined)}
        />
      )}
    </Box>
  );
});
